/**
 * Node Dashboard Component
 *
 * Provides a comprehensive dashboard for monitoring remote compute nodes.
 * Features:
 * - Real-time node status and metrics via WebSocket
 * - Node list with filtering by status
 * - Container management per node
 * - System resource monitoring (CPU, Memory, GPU)
 * - Headscale VPN health status
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { useWebSocket, type NodeMetricsEvent, type ContainerStatusEvent } from '../../hooks/useWebSocket';
import { Layout } from '../layout/Layout';
import { ContainerList, type ContainerData } from './ContainerList';
import { DownloadProgress } from './DownloadProgress';
import { ErrorList, ErrorToastContainer, ErrorStats } from './ErrorNotifications';

// =============================================================================
// Types
// =============================================================================

/**
 * Node status enum matching backend
 */
type NodeStatus = 'OFFLINE' | 'ONLINE' | 'MAINTENANCE';

/**
 * Hardware information for a node
 */
interface NodeHardwareInfo {
  gpuVendor: string;
  gpuCount: number;
  gpuMemoryGb?: number;
  gpuDevices?: string;
  cudaVersion?: string;
  rocmVersion?: string;
  totalRamGb?: number;
  cpuCores?: number;
  gpuModels?: string[];
}

/**
 * Remote node data
 */
interface RemoteNode {
  id: string;
  name: string;
  headscaleIp: string;
  status: NodeStatus;
  hardware: NodeHardwareInfo;
  isGlobal: boolean;
  tenantId: string | null;
  lastHeartbeat: string | null;
  version: string | null;
  containerCount?: number;
  runningContainerCount?: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Real-time node metrics
 */
interface NodeMetrics {
  nodeId: string;
  cpu: number;
  memory: number;
  gpu?: {
    utilization: number;
    memoryUsed: number;
    memoryTotal: number;
  };
  timestamp: string;
}

/**
 * Node statistics response
 */
interface NodeStats {
  totalNodes: number;
  onlineNodes: number;
  offlineNodes: number;
  totalContainers: number;
  runningContainers: number;
  byStatus: Record<NodeStatus, number>;
  byContainerType: Record<string, number>;
}

/**
 * API response types
 */
interface NodesResponse {
  nodes: RemoteNode[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

interface ContainersResponse {
  containers: ContainerData[];
}

/**
 * Status filter type
 */
type StatusFilter = 'all' | 'online' | 'offline' | 'maintenance';

/**
 * Selected node state
 */
interface SelectedNodeState {
  node: RemoteNode;
  containers: ContainerData[];
  isLoadingContainers: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const STATUS_COLORS: Record<NodeStatus, { bg: string; text: string; dot: string; border: string }> = {
  ONLINE: { bg: 'bg-green-900/20', text: 'text-green-400', dot: 'bg-green-400', border: 'border-green-500' },
  OFFLINE: { bg: 'bg-gray-900/20', text: 'text-gray-400', dot: 'bg-gray-400', border: 'border-gray-500' },
  MAINTENANCE: { bg: 'bg-yellow-900/20', text: 'text-yellow-400', dot: 'bg-yellow-400', border: 'border-yellow-500' },
};

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Format timestamp for display
 */
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return 'Never';

  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

/**
 * Format memory in GB
 */
function formatGb(gb?: number): string {
  if (gb === undefined || gb === null) return 'N/A';
  return `${gb} GB`;
}

/**
 * Format percentage
 */
function formatPercent(value: number): string {
  return `${Math.round(value)}%`;
}

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Status badge component
 */
function StatusBadge({ status }: { status: NodeStatus }) {
  const colors = STATUS_COLORS[status];

  return (
    <div
      className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bg} ${colors.text}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${colors.dot}`} />
      {status}
    </div>
  );
}

/**
 * Statistics card component
 */
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'default' | 'green' | 'red' | 'yellow' | 'blue';
}

function StatCard({ title, value, icon, color = 'default' }: StatCardProps) {
  const colorClasses = {
    default: 'text-white',
    green: 'text-green-400',
    red: 'text-red-400',
    yellow: 'text-yellow-400',
    blue: 'text-blue-400',
  };

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-400">{title}</p>
          <p className={`mt-1 text-2xl font-bold ${colorClasses[color]}`}>
            {value}
          </p>
        </div>
        <div className="rounded-full bg-discord-light p-2.5 text-gray-400">
          {icon}
        </div>
      </div>
    </div>
  );
}

/**
 * Metrics bar component
 */
interface MetricsBarProps {
  label: string;
  value: number;
  color: 'green' | 'yellow' | 'red' | 'blue';
}

function MetricsBar({ label, value, color }: MetricsBarProps) {
  const colorClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    red: 'bg-red-500',
    blue: 'bg-blue-500',
  };

  const barColor = value > 90 ? 'bg-red-500' : value > 70 ? 'bg-yellow-500' : colorClasses[color];

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-gray-400">{label}</span>
        <span className="text-white font-medium">{formatPercent(value)}</span>
      </div>
      <div className="h-2 bg-discord-light rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all duration-500`}
          style={{ width: `${Math.min(value, 100)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Node card component
 */
interface NodeCardProps {
  node: RemoteNode;
  metrics?: NodeMetrics;
  isSelected: boolean;
  onClick: () => void;
}

function NodeCard({ node, metrics, isSelected, onClick }: NodeCardProps) {
  const colors = STATUS_COLORS[node.status];

  return (
    <button
      onClick={onClick}
      className={`w-full text-left rounded-lg bg-discord-darker border p-4 transition-all hover:shadow-lg ${
        isSelected
          ? `${colors.border} border-2 ring-2 ring-${colors.border.replace('border-', '')}/20`
          : 'border-discord-light hover:border-discord-blurple'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white truncate">{node.name}</h3>
          <p className="mt-0.5 text-xs text-gray-500 font-mono">{node.headscaleIp}</p>
        </div>
        <StatusBadge status={node.status} />
      </div>

      {/* Hardware Info */}
      <div className="mt-3 flex items-center gap-3 text-xs text-gray-400">
        {node.hardware.gpuVendor !== 'none' && (
          <div className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
            <span>{node.hardware.gpuCount}x {node.hardware.gpuVendor.toUpperCase()}</span>
          </div>
        )}
        {node.hardware.totalRamGb && (
          <div className="flex items-center gap-1">
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <span>{formatGb(node.hardware.totalRamGb)}</span>
          </div>
        )}
      </div>

      {/* Real-time Metrics */}
      {node.status === 'ONLINE' && metrics && (
        <div className="mt-3 space-y-2">
          <MetricsBar label="CPU" value={metrics.cpu} color="blue" />
          <MetricsBar label="Memory" value={metrics.memory} color="green" />
          {metrics.gpu && (
            <MetricsBar label="GPU" value={metrics.gpu.utilization} color="yellow" />
          )}
        </div>
      )}

      {/* Container Stats */}
      {(node.containerCount !== undefined || node.runningContainerCount !== undefined) && (
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-gray-500">
            {node.containerCount ?? 0} container{node.containerCount === 1 ? '' : 's'}
          </span>
          {(node.runningContainerCount ?? 0) > 0 && (
            <span className="text-green-400">{node.runningContainerCount} running</span>
          )}
        </div>
      )}

      {/* Last Heartbeat */}
      <div className="mt-2 text-xs text-gray-500">
        Last seen: {formatTimestamp(node.lastHeartbeat)}
      </div>
    </button>
  );
}

/**
 * Status filter buttons
 */
interface StatusFilterProps {
  value: StatusFilter;
  onChange: (filter: StatusFilter) => void;
  counts: Record<StatusFilter, number>;
}

function StatusFilterButtons({ value, onChange, counts }: StatusFilterProps) {
  const filters: { key: StatusFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'online', label: 'Online' },
    { key: 'offline', label: 'Offline' },
    { key: 'maintenance', label: 'Maintenance' },
  ];

  return (
    <div className="flex items-center gap-2">
      {filters.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
            value === key
              ? 'bg-discord-blurple text-white'
              : 'bg-discord-light text-gray-300 hover:text-white'
          }`}
        >
          {label}
          {counts[key] > 0 && (
            <span
              className={`ml-1.5 rounded-full px-1.5 py-0.5 text-xs ${
                value === key ? 'bg-white/20' : 'bg-discord-darker'
              }`}
            >
              {counts[key]}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

/**
 * Node detail panel component
 */
interface NodeDetailPanelProps {
  selectedNode: SelectedNodeState | null;
  metrics?: NodeMetrics;
  onClose: () => void;
  onContainerChange: () => void;
  canManage: boolean;
}

function NodeDetailPanel({
  selectedNode,
  metrics,
  onClose,
  onContainerChange,
  canManage,
}: NodeDetailPanelProps) {
  if (!selectedNode) {
    return (
      <div className="rounded-lg bg-discord-darker border border-discord-light p-6 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-500 mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
          />
        </svg>
        <h3 className="text-lg font-medium text-white">Select a Node</h3>
        <p className="mt-1 text-sm text-gray-400">
          Click on a node card to view details and manage containers
        </p>
      </div>
    );
  }

  const { node, containers, isLoadingContainers } = selectedNode;
  const colors = STATUS_COLORS[node.status];

  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light overflow-hidden">
      {/* Header */}
      <div className={`p-4 border-b ${colors.border}`}>
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <h2 className="text-xl font-bold text-white">{node.name}</h2>
            <p className="text-sm text-gray-400 font-mono mt-1">{node.headscaleIp}</p>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={node.status} />
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Node Info */}
      <div className="p-4 border-b border-discord-light">
        <h3 className="text-sm font-medium text-gray-400 mb-3">Hardware</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-gray-500">GPU</p>
            <p className="text-sm text-white">
              {node.hardware.gpuVendor !== 'none'
                ? `${node.hardware.gpuCount}x ${node.hardware.gpuVendor.toUpperCase()}`
                : 'None'}
            </p>
          </div>
          {node.hardware.gpuMemoryGb && (
            <div>
              <p className="text-xs text-gray-500">GPU Memory</p>
              <p className="text-sm text-white">{formatGb(node.hardware.gpuMemoryGb)}</p>
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500">RAM</p>
            <p className="text-sm text-white">{formatGb(node.hardware.totalRamGb)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">CPU Cores</p>
            <p className="text-sm text-white">{node.hardware.cpuCores ?? 'N/A'}</p>
          </div>
          {node.hardware.cudaVersion && (
            <div>
              <p className="text-xs text-gray-500">CUDA</p>
              <p className="text-sm text-white">{node.hardware.cudaVersion}</p>
            </div>
          )}
          {node.hardware.rocmVersion && (
            <div>
              <p className="text-xs text-gray-500">ROCm</p>
              <p className="text-sm text-white">{node.hardware.rocmVersion}</p>
            </div>
          )}
          {node.version && (
            <div>
              <p className="text-xs text-gray-500">Node Version</p>
              <p className="text-sm text-white">{node.version}</p>
            </div>
          )}
        </div>
      </div>

      {/* Real-time Metrics */}
      {node.status === 'ONLINE' && metrics && (
        <div className="p-4 border-b border-discord-light">
          <h3 className="text-sm font-medium text-gray-400 mb-3">Real-time Metrics</h3>
          <div className="space-y-3">
            <MetricsBar label="CPU Usage" value={metrics.cpu} color="blue" />
            <MetricsBar label="Memory Usage" value={metrics.memory} color="green" />
            {metrics.gpu && (
              <>
                <MetricsBar label="GPU Utilization" value={metrics.gpu.utilization} color="yellow" />
                <div className="text-xs text-gray-400 flex justify-between">
                  <span>GPU Memory</span>
                  <span>{formatGb(metrics.gpu.memoryUsed / 1024)} / {formatGb(metrics.gpu.memoryTotal / 1024)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Containers */}
      <div className="p-4 border-b border-discord-light">
        <h3 className="text-sm font-medium text-gray-400 mb-3">Containers</h3>
        <ContainerList
          nodeId={node.id}
          containers={containers}
          isLoading={isLoadingContainers}
          onContainerChange={onContainerChange}
          canManage={canManage}
        />
      </div>

      {/* Download Progress */}
      <div className="p-4 border-b border-discord-light">
        <h3 className="text-sm font-medium text-gray-400 mb-3">
          Download Progress
        </h3>
        <DownloadProgress
          nodeId={node.id}
          showCompleted={false}
          maxItems={5}
          compact
        />
      </div>

      {/* Recent Errors */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-gray-400">Recent Errors</h3>
          <ErrorStats nodeId={node.id} />
        </div>
        <ErrorList
          nodeId={node.id}
          showResolved={false}
          pageSize={5}
        />
      </div>
    </div>
  );
}

/**
 * Loading skeleton component
 */
function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-6">
      {/* Stats skeleton */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
            <div className="h-4 w-24 bg-discord-light rounded mb-2" />
            <div className="h-8 w-12 bg-discord-light rounded" />
          </div>
        ))}
      </div>

      {/* Content skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1 space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="rounded-lg bg-discord-darker border border-discord-light p-4">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <div className="h-5 w-32 bg-discord-light rounded" />
                  <div className="h-3 w-24 bg-discord-light rounded" />
                </div>
                <div className="h-6 w-16 bg-discord-light rounded-full" />
              </div>
              <div className="mt-4 space-y-2">
                <div className="h-2 w-full bg-discord-light rounded" />
                <div className="h-2 w-full bg-discord-light rounded" />
              </div>
            </div>
          ))}
        </div>
        <div className="lg:col-span-2 rounded-lg bg-discord-darker border border-discord-light p-6">
          <div className="h-8 w-48 bg-discord-light rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-12 bg-discord-light rounded" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Icons
// =============================================================================

const ServerIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
    />
  </svg>
);

const OnlineIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const ContainerIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
    />
  </svg>
);

const GpuIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
  </svg>
);

// =============================================================================
// Register Node Modal
// =============================================================================

interface RegisterNodeFormData {
  name: string;
  headscaleIp: string;
  gpuVendor: string;
  gpuCount: number;
  gpuMemoryGb: string;
  totalRamGb: string;
  cpuCores: string;
  cudaVersion: string;
  isGlobal: boolean;
}

interface RegisterNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RegisterNodeFormData) => Promise<void>;
  isSubmitting: boolean;
}

function RegisterNodeModal({ isOpen, onClose, onSubmit, isSubmitting }: RegisterNodeModalProps) {
  const [formData, setFormData] = useState<RegisterNodeFormData>({
    name: '',
    headscaleIp: '',
    gpuVendor: 'nvidia',
    gpuCount: 1,
    gpuMemoryGb: '',
    totalRamGb: '',
    cpuCores: '',
    cudaVersion: '',
    isGlobal: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (!/^[a-zA-Z0-9][a-zA-Z0-9\-_]*$/.test(formData.name)) {
      newErrors.name = 'Name must start with alphanumeric and contain only alphanumeric, hyphens, and underscores';
    }

    if (!formData.headscaleIp.trim()) {
      newErrors.headscaleIp = 'Headscale IP is required';
    } else if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(formData.headscaleIp)) {
      newErrors.headscaleIp = 'Invalid IP address format';
    }

    if (formData.gpuCount < 0) {
      newErrors.gpuCount = 'GPU count must be 0 or more';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    await onSubmit(formData);
  };

  const updateField = (field: keyof RegisterNodeFormData, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/60" onClick={onClose} />
        <div className="relative w-full max-w-lg rounded-lg bg-discord-dark border border-discord-light p-6 shadow-xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Register New Node</h2>
            <button
              onClick={onClose}
              className="rounded-md p-1 text-gray-400 hover:text-white transition-colors"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Node Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Node Name <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                placeholder="gpu-node-01"
                className={`w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
                  errors.name ? 'border-red-500 focus:ring-red-500' : 'border-discord-light focus:ring-discord-blurple'
                }`}
              />
              {errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}
            </div>

            {/* Headscale IP */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1">
                Headscale IP <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                value={formData.headscaleIp}
                onChange={(e) => updateField('headscaleIp', e.target.value)}
                placeholder="100.64.0.1"
                className={`w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
                  errors.headscaleIp ? 'border-red-500 focus:ring-red-500' : 'border-discord-light focus:ring-discord-blurple'
                }`}
              />
              {errors.headscaleIp && <p className="mt-1 text-xs text-red-400">{errors.headscaleIp}</p>}
              <p className="mt-1 text-xs text-gray-500">The IP address assigned by Headscale VPN</p>
            </div>

            {/* Hardware Section */}
            <div className="border-t border-discord-light pt-4">
              <h3 className="text-sm font-medium text-gray-300 mb-3">Hardware Information</h3>

              <div className="grid grid-cols-2 gap-4">
                {/* GPU Vendor */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">GPU Vendor</label>
                  <select
                    value={formData.gpuVendor}
                    onChange={(e) => updateField('gpuVendor', e.target.value)}
                    className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  >
                    <option value="nvidia">NVIDIA</option>
                    <option value="amd">AMD</option>
                    <option value="intel">Intel</option>
                    <option value="none">None (CPU only)</option>
                  </select>
                </div>

                {/* GPU Count */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">GPU Count</label>
                  <input
                    type="number"
                    value={formData.gpuCount}
                    onChange={(e) => updateField('gpuCount', parseInt(e.target.value) || 0)}
                    min={0}
                    max={16}
                    className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* GPU Memory */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">GPU Memory (GB)</label>
                  <input
                    type="number"
                    value={formData.gpuMemoryGb}
                    onChange={(e) => updateField('gpuMemoryGb', e.target.value)}
                    placeholder="24"
                    className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* System RAM */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">System RAM (GB)</label>
                  <input
                    type="number"
                    value={formData.totalRamGb}
                    onChange={(e) => updateField('totalRamGb', e.target.value)}
                    placeholder="64"
                    className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* CPU Cores */}
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">CPU Cores</label>
                  <input
                    type="number"
                    value={formData.cpuCores}
                    onChange={(e) => updateField('cpuCores', e.target.value)}
                    placeholder="16"
                    className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                  />
                </div>

                {/* CUDA Version */}
                {formData.gpuVendor === 'nvidia' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-1">CUDA Version</label>
                    <input
                      type="text"
                      value={formData.cudaVersion}
                      onChange={(e) => updateField('cudaVersion', e.target.value)}
                      placeholder="12.1"
                      className="w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-discord-blurple"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Global Toggle */}
            <div className="flex items-center justify-between pt-2">
              <div>
                <span className="text-sm font-medium text-gray-300">Global Node</span>
                <p className="text-xs text-gray-500">Available to all tenants</p>
              </div>
              <button
                type="button"
                onClick={() => updateField('isGlobal', !formData.isGlobal)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.isGlobal ? 'bg-discord-blurple' : 'bg-discord-light'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.isGlobal ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t border-discord-light">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80 transition-colors disabled:opacity-50"
              >
                {isSubmitting ? 'Registering...' : 'Register Node'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Node Dashboard page component
 *
 * Displays:
 * - Overview statistics (total nodes, online, containers)
 * - List of nodes with status and real-time metrics
 * - Container management for selected node
 * - WebSocket-based real-time updates
 */
export function NodeDashboard() {
  const { user, accessToken } = useAuth();
  const { effectiveTenantId, isGlobalAdmin } = useTenant();

  // State
  const [nodes, setNodes] = useState<RemoteNode[]>([]);
  const [stats, setStats] = useState<NodeStats | null>(null);
  const [nodeMetrics, setNodeMetrics] = useState<Map<string, NodeMetrics>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [selectedNode, setSelectedNode] = useState<SelectedNodeState | null>(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);

  // WebSocket for real-time metrics
  const { isConnected, subscribe } = useWebSocket({
    accessToken,
    autoConnect: true,
  });

  /**
   * Fetch nodes and stats from API
   */
  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    const apiClient = getApiClient();

    try {
      const queryParams = effectiveTenantId
        ? `?includeGlobal=true`
        : '?includeGlobal=true';

      // Fetch nodes and stats in parallel
      const [nodesResult, statsResult] = await Promise.all([
        apiClient.get<NodesResponse>(`/nodes${queryParams}`),
        apiClient.get<NodeStats>('/nodes/stats'),
      ]);

      if (isSuccess(nodesResult)) {
        setNodes(nodesResult.data.nodes);
      } else {
        setError(nodesResult.error.message || 'Failed to fetch nodes');
      }

      if (isSuccess(statsResult)) {
        setStats(statsResult.data);
      }
    } catch (err) {
      setError('Failed to load node data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [effectiveTenantId]);

  /**
   * Fetch containers for a node
   */
  const fetchContainers = useCallback(async (nodeId: string): Promise<ContainerData[]> => {
    const apiClient = getApiClient();
    const result = await apiClient.get<ContainersResponse>(`/nodes/${nodeId}/containers`);

    if (isSuccess(result)) {
      return result.data.containers;
    }
    return [];
  }, []);

  /**
   * Handle node registration
   */
  const handleRegisterNode = useCallback(
    async (formData: RegisterNodeFormData) => {
      setIsRegistering(true);
      setError(null);

      const apiClient = getApiClient();

      const payload = {
        name: formData.name,
        headscaleIp: formData.headscaleIp,
        hardware: {
          gpuVendor: formData.gpuVendor,
          gpuCount: formData.gpuCount,
          gpuMemoryGb: formData.gpuMemoryGb ? parseFloat(formData.gpuMemoryGb) : undefined,
          totalRamGb: formData.totalRamGb ? parseFloat(formData.totalRamGb) : undefined,
          cpuCores: formData.cpuCores ? parseInt(formData.cpuCores, 10) : undefined,
          cudaVersion: formData.cudaVersion || undefined,
        },
        isGlobal: formData.isGlobal,
      };

      try {
        const result = await apiClient.post<{ node: RemoteNode; apiKey: string }>('/nodes', payload);

        if (isSuccess(result)) {
          // Refresh the nodes list
          await fetchData();
          setShowRegisterModal(false);
          // Could show the API key here - it's in result.data.apiKey
        } else {
          setError(result.error.message || 'Failed to register node');
        }
      } catch {
        setError('Failed to register node. Please try again.');
      } finally {
        setIsRegistering(false);
      }
    },
    [fetchData]
  );

  /**
   * Handle node selection
   */
  const handleNodeSelect = useCallback(
    async (node: RemoteNode) => {
      if (selectedNode?.node.id === node.id) {
        // Deselect if clicking the same node
        setSelectedNode(null);
        return;
      }

      setSelectedNode({
        node,
        containers: [],
        isLoadingContainers: true,
      });

      const containers = await fetchContainers(node.id);
      setSelectedNode((prev) =>
        prev?.node.id === node.id
          ? { ...prev, containers, isLoadingContainers: false }
          : prev
      );
    },
    [selectedNode, fetchContainers]
  );

  /**
   * Handle container change (refresh containers)
   */
  const handleContainerChange = useCallback(async () => {
    if (!selectedNode) return;

    const containers = await fetchContainers(selectedNode.node.id);
    setSelectedNode((prev) =>
      prev ? { ...prev, containers } : prev
    );

    // Refresh stats
    const apiClient = getApiClient();
    const statsResult = await apiClient.get<NodeStats>('/nodes/stats');
    if (isSuccess(statsResult)) {
      setStats(statsResult.data);
    }
  }, [selectedNode, fetchContainers]);

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  /**
   * Subscribe to real-time node metrics
   */
  useEffect(() => {
    const unsubscribeMetrics = subscribe<NodeMetricsEvent>('node:metrics', (data) => {
      setNodeMetrics((prev) => {
        const next = new Map(prev);
        next.set(data.nodeId, data);
        return next;
      });
    });

    const unsubscribeContainer = subscribe<ContainerStatusEvent>('container:status', () => {
      // Refresh containers when status changes
      void handleContainerChange();
    });

    return () => {
      unsubscribeMetrics();
      unsubscribeContainer();
    };
  }, [subscribe, handleContainerChange]);

  /**
   * Calculate status counts
   */
  const statusCounts = useMemo(() => {
    return {
      all: nodes.length,
      online: nodes.filter((n) => n.status === 'ONLINE').length,
      offline: nodes.filter((n) => n.status === 'OFFLINE').length,
      maintenance: nodes.filter((n) => n.status === 'MAINTENANCE').length,
    };
  }, [nodes]);

  /**
   * Filter nodes by status
   */
  const filteredNodes = useMemo(() => {
    if (statusFilter === 'all') return nodes;
    return nodes.filter(
      (node) => node.status.toLowerCase() === statusFilter
    );
  }, [nodes, statusFilter]);

  /**
   * Calculate total GPU count
   */
  const totalGpuCount = useMemo(() => {
    return nodes.reduce((sum, node) => {
      if (node.hardware.gpuVendor !== 'none') {
        return sum + node.hardware.gpuCount;
      }
      return sum;
    }, 0);
  }, [nodes]);

  /**
   * Create node names map for display in progress/error components
   */
  const nodeNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    nodes.forEach((node) => map.set(node.id, node.name));
    return map;
  }, [nodes]);

  /**
   * Check if user can manage nodes
   */
  const canManageNodes = useMemo(() => {
    return isGlobalAdmin || user?.role === 'TENANT_ADMIN';
  }, [isGlobalAdmin, user?.role]);

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Node Dashboard</h1>
            <p className="mt-1 text-gray-400">
              Monitor and manage remote compute nodes
              {isConnected && (
                <span className="ml-2 inline-flex items-center gap-1 text-green-400">
                  <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
                  Live
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canManageNodes && (
              <button
                onClick={() => setShowRegisterModal(true)}
                className="inline-flex items-center gap-2 rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/80 transition-colors"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Register Node
              </button>
            )}
            <button
              onClick={() => void fetchData()}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-discord-light/80 transition-colors disabled:opacity-50"
            >
              <svg
                className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{error}</p>
              </div>
              <button
                onClick={() => void fetchData()}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <LoadingSkeleton />
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard
                title="Total Nodes"
                value={stats?.totalNodes ?? nodes.length}
                icon={<ServerIcon />}
              />
              <StatCard
                title="Online"
                value={stats?.onlineNodes ?? statusCounts.online}
                icon={<OnlineIcon />}
                color="green"
              />
              <StatCard
                title="Running Containers"
                value={stats?.runningContainers ?? 0}
                icon={<ContainerIcon />}
                color="blue"
              />
              <StatCard
                title="Total GPUs"
                value={totalGpuCount}
                icon={<GpuIcon />}
                color="yellow"
              />
            </div>

            {/* Filters */}
            <StatusFilterButtons
              value={statusFilter}
              onChange={setStatusFilter}
              counts={statusCounts}
            />

            {/* Main Content */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Node List */}
              <div className="lg:col-span-1 space-y-4">
                {filteredNodes.length === 0 ? (
                  <div className="rounded-lg bg-discord-darker border border-discord-light p-6 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-500 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01"
                      />
                    </svg>
                    <h3 className="text-lg font-medium text-white">No nodes found</h3>
                    <p className="mt-1 text-sm text-gray-400">
                      {statusFilter === 'all'
                        ? 'No compute nodes have been registered yet'
                        : `No ${statusFilter} nodes`}
                    </p>
                  </div>
                ) : (
                  filteredNodes.map((node) => (
                    <NodeCard
                      key={node.id}
                      node={node}
                      metrics={nodeMetrics.get(node.id)}
                      isSelected={selectedNode?.node.id === node.id}
                      onClick={() => void handleNodeSelect(node)}
                    />
                  ))
                )}
              </div>

              {/* Node Detail Panel */}
              <div className="lg:col-span-2">
                <NodeDetailPanel
                  selectedNode={selectedNode}
                  metrics={selectedNode ? nodeMetrics.get(selectedNode.node.id) : undefined}
                  onClose={() => setSelectedNode(null)}
                  onContainerChange={handleContainerChange}
                  canManage={canManageNodes}
                />
              </div>
            </div>

            {/* Summary */}
            <div className="text-sm text-gray-400">
              Showing {filteredNodes.length} of {nodes.length} nodes ·{' '}
              {stats?.totalContainers ?? 0} total containers
            </div>
          </>
        )}
      </div>

      {/* Register Node Modal */}
      <RegisterNodeModal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        onSubmit={handleRegisterNode}
        isSubmitting={isRegistering}
      />

      {/* Real-time Error Toast Notifications */}
      <ErrorToastContainer
        maxToasts={5}
        autoDismissMs={10000}
        nodeNames={nodeNamesMap}
      />
    </Layout>
  );
}

export default NodeDashboard;
